import { useCallback, useEffect, useRef, useState } from "react";
import CosmicIcon from "./CosmicIcon";
import styles from "./InstallPrompt.module.css";

/**
 * Controlled PWA install prompt.
 *
 * We capture the browser's `beforeinstallprompt` (suppressing the default,
 * intrusive mini-infobar) and surface our own banner at a high-intent moment:
 *   - after the user has spent 2 minutes on the site, OR
 *   - right after they finish a chat (whichever comes first).
 *
 * It is shown at most once. If dismissed we back off for 14 days; if installed
 * (or already running as an installed app) we never show it again.
 */

const STORAGE_KEY = "aura_install_prompt_v1";
const DWELL_MS = 2 * 60 * 1000; // 2 minutes on site
const COOLDOWN_MS = 2 * 24 * 60 * 60 * 1000; // 2 days after a dismissal

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isInstalled(): boolean {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    // iOS Safari
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function mayShow(): boolean {
  // Shown at most once per page-load (see shownRef); after a dismissal it backs
  // off for COOLDOWN_MS. A completed install permanently silences it.
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return true;
    const s = JSON.parse(raw) as { installed?: boolean; dismissedAt?: number };
    if (s.installed) return false;
    if (s.dismissedAt && Date.now() - s.dismissedAt < COOLDOWN_MS) return false;
    return true;
  } catch {
    return true;
  }
}

function remember(state: { installed?: boolean; dismissedAt?: number }) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

export default function InstallPrompt() {
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);
  const shownRef = useRef(false);
  const dwellReachedRef = useRef(false);
  const [visible, setVisible] = useState(false);

  const maybeShow = useCallback(() => {
    if (shownRef.current) return; // never nag twice in one session
    if (!deferredRef.current) return; // browser hasn't offered install yet
    if (isInstalled()) return;
    if (!mayShow()) return;
    shownRef.current = true;
    setVisible(true);
  }, []);

  useEffect(() => {
    if (isInstalled()) return;

    const onBeforeInstall = (e: Event) => {
      // Suppress Chrome's default banner; we decide when to ask.
      e.preventDefault();
      deferredRef.current = e as BeforeInstallPromptEvent;
      // If the 2-minute mark already passed before install became available.
      if (dwellReachedRef.current) maybeShow();
    };
    const onInstalled = () => {
      remember({ installed: true });
      setVisible(false);
    };
    const onChatCompleted = () => maybeShow();

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("aura:chat-completed", onChatCompleted);

    const timer = window.setTimeout(() => {
      dwellReachedRef.current = true;
      maybeShow();
    }, DWELL_MS);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("aura:chat-completed", onChatCompleted);
      window.clearTimeout(timer);
    };
  }, [maybeShow]);

  const handleInstall = useCallback(async () => {
    const dp = deferredRef.current;
    setVisible(false);
    if (!dp) return;
    try {
      await dp.prompt();
      const choice = await dp.userChoice;
      remember(
        choice.outcome === "accepted"
          ? { installed: true }
          : { dismissedAt: Date.now() }
      );
    } catch {
      remember({ dismissedAt: Date.now() });
    }
    deferredRef.current = null;
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    remember({ dismissedAt: Date.now() });
  }, []);

  if (!visible) return null;

  return (
    <div className={styles.wrap} role="dialog" aria-label="Install Aura AI">
      <div className={styles.card}>
        <span className={styles.icon}>
          <CosmicIcon size={40} />
        </span>
        <div className={styles.body}>
          <div className={styles.title}>Install Aura AI</div>
          <div className={styles.sub}>
            Add to your home screen for instant, full-screen access.
          </div>
        </div>
        <button
          type="button"
          className={styles.install}
          onClick={handleInstall}
        >
          Install
        </button>
        <button
          type="button"
          className={styles.close}
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
